import { formatLocalTime } from '../agency-format';
import { formatCount, formatUsd } from '@/features/marketing/analytics/analytics-format';
import type {
  AdsDoctorBoardTotals,
  AdsDoctorFix,
  AdsDoctorMeasurement,
  AdsDoctorRange,
  AdsDoctorRealCost,
  AdsDoctorStatus,
  AdsPlatform,
} from './ads-doctor-types';

/**
 * Ads Doctor display rules shared by the board and the salon check-up. Money is whole dollars; came-in counted only
 * by Hayven Tap is a lower bound ("4+"); revenue the salon's setup can't know is hidden, never shown as $0.
 */

export { formatCount, formatUsd };

export const ADS_DOCTOR_RANGE_OPTIONS: ReadonlyArray<{ value: AdsDoctorRange; label: string }> = [
  { value: 'last_7', label: 'Last 7 days' },
  { value: 'last_30', label: 'Last 30 days' },
  { value: 'last_90', label: 'Last 90 days' },
];

export const DEFAULT_ADS_DOCTOR_RANGE: AdsDoctorRange = 'last_30';

export function isAdsDoctorRange(value: unknown): value is AdsDoctorRange {
  return ADS_DOCTOR_RANGE_OPTIONS.some((option) => option.value === value);
}

/** Calm status palette: tangerine / gold / blue / green / grey — nothing here is an error, so no red. */
export const ADS_DOCTOR_STATUS: Record<AdsDoctorStatus, { label: string; className: string }> = {
  NEEDS_FIX: { label: 'Needs fix', className: 'bg-[var(--c-attention-bg)] text-[var(--c-attention-fg)]' },
  WATCH: { label: 'Watch', className: 'bg-[var(--butter-100)] text-[#5c3d00]' },
  SALON_FOLLOW_UP: { label: 'Salon follow-up', className: 'bg-[var(--sky-100)] text-[var(--sky-700)]' },
  HEALTHY: { label: 'Healthy', className: 'bg-[var(--c-success-soft)] text-[var(--c-success-soft-fg)]' },
  NO_DATA: { label: 'No data', className: 'bg-clovie-sunk text-clovie-muted' },
};

// Re-exported: board filter chips keep one import path.
export { ADS_DOCTOR_BOARD_FILTERS, countStatusFilters, type AdsDoctorBoardFilter } from './ads-doctor-board-filters';

export function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

/** "$38 per new client" / "$12 per sign-up". */
export function formatRealCost(realCost: AdsDoctorRealCost): string {
  return `${formatUsd(realCost.cents)} per ${realCost.per === 'new_client' ? 'new client' : 'sign-up'}`;
}

/**
 * A salon's real cost is "high" at 2× or more of the agency median for the same unit (per new client vs per sign-up
 * are never compared with each other). No median (too few salons) → never high.
 */
export function isRealCostHigh(realCost: AdsDoctorRealCost | null, totals: AdsDoctorBoardTotals): boolean {
  if (!realCost) return false;
  const median = realCost.per === 'new_client' ? totals.medianCostPerNewClientCents : totals.medianCostPerSignUpCents;
  return median !== null && median > 0 && realCost.cents >= 2 * median;
}

/** "12", or "4+" for a lower bound ("at least 4"). Zero stays "0": "0+" reads like a typo, and 0 is still true. */
export function formatLowerBoundCount(count: number, lowerBound: boolean): string {
  return lowerBound && count > 0 ? `${formatCount(count)}+` : formatCount(count);
}

/** "12" / "4+" (Tap-only came-in is a lower bound). */
export function formatCameInCount(count: number, measurement: AdsDoctorMeasurement): string {
  return formatLowerBoundCount(count, measurement.cameIn === 'tap_only_lower_bound');
}

export const TAP_ONLY_CAME_IN_HINT = 'Counted by Hayven Tap — some clients may have come in without tapping.';

/** Revenue only when the salon's setup knows it: "$1,240", "$1,240 est.", or null (unknown — hide it, never $0). */
export function formatRevenue(cents: number | null, measurement: AdsDoctorMeasurement): string | null {
  if (cents === null || measurement.revenue === 'unknown') return null;
  return measurement.revenue === 'estimated' ? `${formatUsd(cents)} est.` : formatUsd(cents);
}

/** "12.5%" — at most one decimal. */
export function formatPercent(value: number): string {
  return `${Number(value.toFixed(1))}%`;
}

function startOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** "today 6:10 AM" / "yesterday 6:10 AM" / "Oct 20" (year added when it differs). */
export function formatCheckedWhen(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const today = startOfLocalDay(now);
  const day = startOfLocalDay(date);
  if (day === today) return `today ${formatLocalTime(date)}`;
  if (day === startOfLocalDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1))) {
    return `yesterday ${formatLocalTime(date)}`;
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/** A calendar date "2026-10-01" as "Oct 1" — parsed by parts, so no timezone can move it a day. */
export function formatIsoDay(isoDay: string): string {
  const [year, month, day] = isoDay.split('-').map(Number);
  if (!year || !month || !day) return isoDay;
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** "Oct 1 – Oct 30" — the days an AI check-up looked at. */
export function formatCheckupWindow(from: string, to: string): string {
  return `${formatIsoDay(from)} – ${formatIsoDay(to)}`;
}

/** Ads Manager for one ad account ("act_123" or "123" both work). */
export function adsManagerUrl(adAccountId: string): string {
  const id = adAccountId.replace(/^act_/, '');
  return `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${encodeURIComponent(id)}`;
}

export const FIX_VERDICT: Record<NonNullable<AdsDoctorFix['verdict']>, { label: string; className: string }> = {
  PENDING: { label: 'Measuring', className: 'bg-clovie-sunk text-clovie-muted' },
  HELPED: { label: 'Helped', className: 'bg-[var(--c-success-soft)] text-[var(--c-success-soft-fg)]' },
  NO_CHANGE: { label: 'No change', className: 'bg-clovie-sunk text-clovie-text-2' },
  WORSE: { label: 'Got worse', className: 'bg-[var(--c-attention-bg)] text-[var(--c-attention-fg)]' },
};

type JudgedOn = NonNullable<AdsDoctorFix['judgedOn']>;

const METRIC_LABEL: Record<string, string> = {
  costPerSignUpCents: 'Cost per sign-up',
  signUpsPerDay: 'Sign-ups a day',
  pageViewToSignUpPercent: 'Page visits → sign-ups',
  ctrPercent: 'Click rate',
  frequency: 'Times each person saw the ad',
  metaSpendCents: 'Spend reaching Hayven pages',
};

function formatMetricValue(metric: string, value: number): string {
  if (metric.endsWith('Cents')) return formatUsd(value);
  if (metric.endsWith('Percent')) return formatPercent(value);
  if (metric === 'signUpsPerDay') return `${Number(value.toFixed(1))}/day`;
  return String(Number(value.toFixed(1)));
}

/** Google fixes are judged by Hayven's own checks: the daily check once its numbers settle, or the next AI review. */
const GOOGLE_PENDING = 'Hayven’s next checks judge it once the change has had time to show';

/** The verdict chip: a Google fix closed without a verdict is "Not judged", not "No change". */
export function fixVerdictChip(fix: AdsDoctorFix): { label: string; className: string } {
  if (fix.notJudged) return { label: 'Not judged', className: FIX_VERDICT.PENDING.className };
  return FIX_VERDICT[fix.verdict ?? 'PENDING'];
}

/**
 * The one line under a judged fix: "Cost per sign-up: $24 → $15", or why nothing was judged. `still_not_linked` =
 * the ads still don't open a Hayven page (they may be running) — never worded as "ads were off". Google fixes have no
 * before → after number: Hayven's daily check (or the next AI review) looked again after the change could show.
 */
export function describeFixResult(fix: AdsDoctorFix, platform: AdsPlatform = 'meta'): string | null {
  if (platform === 'google') {
    if (fix.notJudged) return 'Not judged: the ads were off or spent too little after the fix';
    if (fix.verdict === 'PENDING' || fix.verdict === null) return GOOGLE_PENDING;
    if (fix.verdict === 'HELPED') return fix.judgedBy === 'review' ? 'The next AI check-up didn’t find it again' : 'Hayven’s daily check doesn’t see it anymore';
    if (fix.verdict === 'NO_CHANGE') return fix.judgedBy === 'review' ? 'The next AI check-up found it again' : 'Hayven’s daily check still sees it';
    return null;
  }
  if (fix.verdict === 'PENDING' || fix.verdict === null) return 'Judged 7 days after the fix';
  const judged: JudgedOn | null = fix.judgedOn;
  if (!judged) return null;
  if (judged.reason === 'still_not_linked') return 'The ads still don’t open a Hayven page';
  if (judged.reason === 'no_spend_after') return 'The ads were off after the fix, so nothing to compare';
  if (judged.reason === 'no_spend_before') return 'No ad spend before the fix, so nothing to compare';
  if (!judged.metric) return null;
  const label = METRIC_LABEL[judged.metric] ?? judged.metric;
  const before = judged.before === null ? '—' : formatMetricValue(judged.metric, judged.before);
  const after = judged.after === null ? '—' : formatMetricValue(judged.metric, judged.after);
  return `${label}: ${before} → ${after}`;
}

// Re-exported: finding labels (evidence source, where to fix, severity) keep one import path.
export { evidenceSourceLabel, FINDING_SEVERITY, fixWhereLabel } from './ads-doctor-finding-format';
